import { AsyncPipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, ViewChild, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import type { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { PinnedCtaComponent } from 'components/molecules/pinned-cta/pinned-cta.component';
import { TopBannerComponent } from 'components/molecules/top-banner/top-banner.component';
import type { IBannerOptions } from 'components/organisms/header-v2/entities/types';
import { HeaderV2Service } from 'components/organisms/header-v2/service/header-v2.service';
import { HeaderViewService } from 'components/organisms/header-v2/service/header-view.service';
import { PageLocalizationService } from 'src/services/page-localization/page-localization.service';
import { applyHeaderConfigFromViewData } from 'src/shared/helpers/apply-layout/apply-header-config-from-view-data';

import type { ILandingPage } from './entities/types';
import { RebrandedHeaderComponent } from './rebranded-header/rebranded-header.component';
import { LayoutService } from './services/layout.service';

@Component({
    selector: 'app-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [RouterOutlet, AsyncPipe, PinnedCtaComponent, TopBannerComponent, RebrandedHeaderComponent],
})
export class LayoutComponent implements OnInit {
    readonly headerService = inject(HeaderV2Service);
    readonly headerViewService = inject(HeaderViewService);
    readonly layoutService = inject(LayoutService);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly pageLocalizationService = inject(PageLocalizationService);

    @ViewChild('footerContainer', { read: ViewContainerRef, static: true })
    public readonly footerContainer: ViewContainerRef;

    @ViewChild('footerSecondaryContainer', { read: ViewContainerRef, static: true })
    public readonly footerSecondaryContainer: ViewContainerRef;

    public bannerOptions: Observable<IBannerOptions>;

    private destroyRef: DestroyRef = inject(DestroyRef);

    public ngOnInit(): void {
        this.bannerOptions = this.headerViewService.options$;

        // Resolvers run before component activation, so the resolved page data is already
        // available in the child route snapshot. Populate header data here (before the header
        // renders) to avoid an empty header and SSR/hydration mismatches.
        this.bootstrapPageShellFromRoute();
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => this.bootstrapPageShellFromRoute());
    }

    private bootstrapPageShellFromRoute(): void {
        const viewData = this.findResolvedViewData();

        if (!viewData?.layout) {
            return;
        }

        this.bootstrapPageShell(viewData);
    }

    public bootstrapPageShell(viewData: ILandingPage): void {
        applyHeaderConfigFromViewData(this.headerViewService, this.layoutService, viewData);
        this.pageLocalizationService.generateLocalisedLinks(viewData);
    }

    private findResolvedViewData(): ILandingPage | undefined {
        let route: ActivatedRoute | null = this.activatedRoute.firstChild;

        while (route) {
            const viewData = route.snapshot.data?.['view'] as ILandingPage | undefined;

            if (viewData?.layout) {
                return viewData;
            }

            route = route.firstChild;
        }

        return undefined;
    }
}
